/**
 * « Partager » un produit — demande de Philippe (06/10/2026) : l'ancien
 * WordPress proposait Facebook / X / Pinterest / mail sous chaque produit.
 *
 * Le lien partagé est `/p/<id>` : une page d'aperçu (photo, nom, prix) lue par
 * les robots de WhatsApp, Facebook, Messages…, qui renvoie un humain sur la
 * fiche. Sur téléphone, la feuille de partage native (WhatsApp, Messages,
 * Facebook, Mail, X… ce que le client a installé) ; sur ordinateur, un petit
 * menu : copier, WhatsApp, Facebook, X, e-mail.
 * Aucun bouton officiel de réseau social : ils poseraient des cookies tiers.
 */
import { useEffect, useRef, useState } from 'react';

interface Props {
  productId: string;
  name: string;
}

export default function PartagerProduit({ productId, name }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [copie, setCopie] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const lien = `${window.location.origin}/p/${productId}`;
  const texte = `${name} — Les Folies Maraîchères`;

  useEffect(() => {
    if (!ouvert) return;
    const fermer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, [ouvert]);

  const partager = async () => {
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title: name, text: texte, url: lien });
      } catch {
        /* annulé */
      }
      return;
    }
    setOuvert((o) => !o);
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {
      window.prompt('Copiez ce lien :', lien);
    }
  };

  const t = encodeURIComponent(texte);
  const u = encodeURIComponent(lien);
  const item = 'flex items-center gap-2 w-full px-4 py-3 text-left text-[15px] text-[#3a3324] hover:bg-[#f6efe2]';

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => void partager()}
        aria-label="Partager ce produit"
        title="Partager ce produit"
        aria-expanded={ouvert}
        className="w-12 h-12 rounded-full bg-white ring-1 ring-black/10 shadow-sm flex items-center justify-center active:scale-95 transition-transform text-[#4a5c3a]"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="M8.6 13.5 15.4 17.5M15.4 6.5 8.6 10.5" />
        </svg>
      </button>
      {ouvert && (
        <div role="menu" className="absolute right-0 top-14 z-20 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/10 overflow-hidden">
          <button type="button" role="menuitem" className={item} onClick={() => void copier()}>
            {copie ? '✓ Lien copié' : '🔗 Copier le lien'}
          </button>
          <a role="menuitem" className={item} href={`https://wa.me/?text=${t}%20${u}`} target="_blank" rel="noopener noreferrer">
            💬 WhatsApp
          </a>
          <a role="menuitem" className={item} href={`https://www.facebook.com/sharer/sharer.php?u=${u}`} target="_blank" rel="noopener noreferrer">
            📘 Facebook
          </a>
          <a role="menuitem" className={item} href={`https://twitter.com/intent/tweet?text=${t}&url=${u}`} target="_blank" rel="noopener noreferrer">
            𝕏 X (Twitter)
          </a>
          <a role="menuitem" className={item} href={`mailto:?subject=${t}&body=${t}%0A${u}`}>
            ✉️ Email
          </a>
        </div>
      )}
    </div>
  );
}
